import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useFindEventQrQuery, useFindOneEventQuery } from 'src/store/eventApi';
import { Spinner } from 'src/ui/Spinner';

const CJK_FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "微软雅黑", "Noto Sans CJK SC", "Source Han Sans SC", "WenQuanYi Micro Hei", sans-serif';

const PRINT_STYLES = `
.qr-poster, .qr-poster * { font-family: ${CJK_FONT_FAMILY}; }
@media print {
  body, html { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .print-page-shell { overflow: visible !important; height: auto !important; max-height: none !important; background: white !important; padding: 0 !important; }
  .qr-poster { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const QrPosterPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const safeEventId = eventId ?? '';
  const skip = !safeEventId;

  // Inject global print styles via a <style> tag — replaces @emotion/react Global
  useEffect(() => {
    const el = document.createElement('style');
    el.setAttribute('data-qr-poster-styles', '1');
    el.textContent = PRINT_STYLES;
    document.head.appendChild(el);
    return () => {
      document.head.removeChild(el);
    };
  }, []);

  const { data: event, isLoading: eventLoading } = useFindOneEventQuery(
    safeEventId,
    { skip }
  );
  const { data: qr, isLoading: qrLoading } = useFindEventQrQuery(safeEventId, {
    skip,
  });

  const loading = eventLoading || qrLoading;

  return (
    <div
      style={{
        height: '100vh',
        overflowY: 'auto',
        background: '#f7fafc',
        paddingTop: 24,
        paddingBottom: 24,
      }}
      className="print-page-shell"
    >
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingTop: 40,
            paddingBottom: 40,
            gap: 8,
          }}
        >
          <Spinner size="sm" />
          <span style={{ color: '#718096', fontSize: 14 }}>Loading…</span>
        </div>
      ) : !event ? (
        <span style={{ padding: '0 24px', color: '#718096' }}>
          Event not found.
        </span>
      ) : !qr ? (
        <span style={{ padding: '0 24px', color: '#718096' }}>
          No sign-up QR available for this event.
        </span>
      ) : (
        <div
          className="qr-poster"
          style={{
            maxWidth: 560,
            margin: '0 auto',
            background: 'white',
            borderRadius: 12,
            padding: '48px 40px',
            textAlign: 'center',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <div
            style={{
              fontSize: 30,
              fontWeight: 700,
              color: '#1a202c',
              marginBottom: 12,
            }}
          >
            {event.title}
          </div>
          <div style={{ fontSize: 16, color: '#4a5568', marginBottom: 4 }}>
            {fmtDate(event.startAt)}
          </div>
          {event.location && (
            <div style={{ fontSize: 16, color: '#4a5568', marginBottom: 28 }}>
              {event.location}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '28px 0',
            }}
          >
            <QRCodeSVG value={qr.payload} size={420} level="M" includeMargin />
          </div>
          <div style={{ fontSize: 16, color: '#4a5568', marginTop: 20 }}>
            Scan with your phone's camera or the Lovellect app to sign up
          </div>
        </div>
      )}
    </div>
  );
};

export default QrPosterPage;
